/**
 * JourneyDashboard.jsx
 * 
 * Dashboard operativa interna per monitorare i journey.
 * Mostra lista journey con stato staff, conferme, richieste e criticità.
 * 
 * DISTINZIONE:
 * - Staff Status = stato operativo interno (questo componente)
 * - Customer Status = stato visibile al cliente
 * - Item Status = stato singolo elemento
 * - Request Status = stato richiesta utente
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Filter, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle, Clock, MapPin, User, Calendar, ExternalLink,
  FileText, MessageSquare, X, Send
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import RoutingOverview from './RoutingOverview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============== HOOKS ==============

function useAdminToken() {
  const [token, setToken] = useState(null);
  
  useEffect(() => {
    const stored = localStorage.getItem('feel_umbria_admin_token');
    setToken(stored);
  }, []);
  
  return token;
}

// ============== COMPONENTS ==============

/**
 * Stats Card - Mostra statistiche aggregate
 */
function StatsCard({ stats, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[1,2,3,4,5,6].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-4 pb-3">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  if (!stats) return null;
  
  const statItems = [
    { label: 'Totale', value: stats.total_journeys, color: 'text-gray-700' },
    { label: 'Con richieste', value: stats.with_open_requests, color: 'text-amber-600' },
    { label: 'Non confermati', value: stats.not_fully_confirmed, color: 'text-orange-600' },
    { label: 'Prossimi 7gg', value: stats.upcoming_7_days, color: 'text-blue-600' },
    { label: 'Criticità', value: stats.critical_issues, color: 'text-red-600' }
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {statItems.map((item, idx) => (
        <Card key={idx}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-gray-500 mb-1">{item.label}</p>
            <p className={`text-xl font-semibold ${item.color}`}>{item.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Filters Bar - Filtri per la lista
 */
function FiltersBar({ filters, onFilterChange, statusOptions, onReset }) {
  const [showFilters, setShowFilters] = useState(false);
  
  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== undefined && v !== '').length;
  
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1"
        >
          <Filter size={14} />
          Filtri
          {activeFiltersCount > 0 && (
            <Badge className="ml-1 bg-umbria-olive text-white text-[10px] px-1.5">
              {activeFiltersCount}
            </Badge>
          )}
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Button>
        
        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onReset} className="text-xs text-gray-500">
            <X size={12} className="mr-1" />
            Reset
          </Button>
        )}
      </div>
      
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
          {/* Staff Status Filter */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Staff Status</label>
            <select
              value={filters.staff_status || ''}
              onChange={(e) => onFilterChange('staff_status', e.target.value || null)}
              className="w-full text-sm border rounded px-2 py-1.5"
            >
              <option value="">Tutti</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          {/* Open Requests Filter */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Richieste aperte</label>
            <select
              value={filters.has_open_requests === true ? 'true' : ''}
              onChange={(e) => onFilterChange('has_open_requests', e.target.value === 'true' ? true : null)}
              className="w-full text-sm border rounded px-2 py-1.5"
            >
              <option value="">Tutti</option>
              <option value="true">Solo con richieste</option>
            </select>
          </div>
          
          {/* Not Confirmed Filter */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Conferme</label>
            <select
              value={filters.not_fully_confirmed === true ? 'true' : ''}
              onChange={(e) => onFilterChange('not_fully_confirmed', e.target.value === 'true' ? true : null)}
              className="w-full text-sm border rounded px-2 py-1.5"
            >
              <option value="">Tutti</option>
              <option value="true">Non completamente confermati</option>
            </select>
          </div>
          
          {/* Upcoming Days Filter */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Partenza imminente</label>
            <select
              value={filters.upcoming_days || ''}
              onChange={(e) => onFilterChange('upcoming_days', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full text-sm border rounded px-2 py-1.5"
            >
              <option value="">Tutti</option>
              <option value="7">Prossimi 7 giorni</option>
              <option value="14">Prossimi 14 giorni</option>
              <option value="30">Prossimi 30 giorni</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Journey Row - Singola riga della dashboard
 */
function JourneyRow({ journey, onViewDetail }) {
  const statusDisplay = journey.staff_status_display || {};
  
  // Confirmation ratio bar
  const confirmRatio = journey.confirmation_ratio || 0;
  const confirmPercent = Math.round(confirmRatio * 100);
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    } catch {
      return '-';
    }
  };
  
  return (
    <div 
      className={`border rounded-lg p-3 hover:bg-gray-50 transition-colors ${
        journey.has_critical_issues ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
      }`}
      data-testid={`journey-row-${journey.journey_code}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        {/* Code & Customer */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-umbria-charcoal">
              {journey.journey_code}
            </span>
            {journey.has_critical_issues && (
              <AlertTriangle size={14} className="text-red-500" />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <User size={10} />
            <span>{journey.customer_name || journey.customer_email || 'N/A'}</span>
          </div>
        </div>
        
        {/* Staff Status Badge */}
        <div className="w-[140px]">
          <Badge className={`${statusDisplay.color || 'bg-gray-100'} text-xs`}>
            {statusDisplay.label_short || journey.staff_status}
          </Badge>
        </div>
        
        {/* Dates */}
        <div className="w-[120px]">
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <Calendar size={10} />
            <span>{formatDate(journey.start_date)}</span>
            {journey.duration_days && (
              <span className="text-gray-400">({journey.duration_days}gg)</span>
            )}
          </div>
        </div>
        
        {/* Base Area */}
        <div className="w-[100px]">
          {journey.base_area && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <MapPin size={10} />
              <span className="truncate">{journey.base_area}</span>
            </div>
          )}
        </div>
        
        {/* Confirmations */}
        <div className="w-[120px]">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  confirmPercent === 100 ? 'bg-green-500' : 
                  confirmPercent >= 50 ? 'bg-amber-500' : 'bg-red-400'
                }`}
                style={{ width: `${confirmPercent}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 w-16">
              {journey.confirmed_items_count}/{journey.total_items_count}
            </span>
          </div>
        </div>
        
        {/* Open Requests */}
        <div className="w-[80px]">
          {journey.open_requests_count > 0 ? (
            <Badge className="bg-amber-50 text-amber-700 text-xs gap-1">
              <MessageSquare size={10} />
              {journey.open_requests_count}
            </Badge>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>
        
        {/* Supplier Replies + Exceptions (real counts) */}
        <div className="w-[80px] flex items-center gap-1">
          {journey.pending_supplier_replies_count > 0 && (
            <Badge className="bg-blue-50 text-blue-600 text-[10px] gap-0.5" title="Attesa fornitore">
              <Send size={8} />
              {journey.pending_supplier_replies_count}
            </Badge>
          )}
          {journey.exception_count > 0 && (
            <Badge className="bg-red-50 text-red-600 text-[10px] gap-0.5" title="Eccezioni">
              <AlertTriangle size={8} />
              {journey.exception_count}
            </Badge>
          )}
          {!journey.pending_supplier_replies_count && !journey.exception_count && (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>
        
        {/* Actions */}
        <div className="w-[80px] text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetail(journey.journey_code)}
            className="text-xs gap-1"
          >
            Dettaglio
            <ExternalLink size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Journey Detail Modal - Dettaglio interno journey
 */
function JourneyDetailModal({ journeyCode, onClose, token }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  
  useEffect(() => {
    if (!journeyCode || !token) return;
    
    setLoading(true);
    fetch(`${API_URL}/api/admin/journey-dashboard/${journeyCode}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setDetail(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [journeyCode, token]);
  
  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey-dashboard/${journeyCode}/status`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        // Refresh detail
        const refreshed = await fetch(`${API_URL}/api/admin/journey-dashboard/${journeyCode}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json());
        setDetail(refreshed);
      }
    } finally {
      setStatusUpdating(false);
    }
  };
  
  if (!journeyCode) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-umbria-charcoal">
              Dettaglio Journey: {journeyCode}
            </h2>
            <p className="text-xs text-gray-500">Vista interna staff</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={18} />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-umbria-olive" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              Errore: {error}
            </div>
          ) : detail ? (
            <div className="space-y-6">
              {/* Dashboard Row Summary */}
              {detail.dashboard_row && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Riepilogo Operativo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Staff Status</p>
                        <Badge className={detail.dashboard_row.staff_status_display?.color}>
                          {detail.dashboard_row.staff_status_display?.label}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Conferme</p>
                        <p className="font-medium">
                          {detail.dashboard_row.confirmed_items_count} / {detail.dashboard_row.total_items_count}
                          <span className="text-gray-400 ml-1">
                            ({Math.round(detail.dashboard_row.confirmation_ratio * 100)}%)
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Richieste aperte</p>
                        <p className="font-medium">{detail.dashboard_row.open_requests_count}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Criticità</p>
                        {detail.dashboard_row.has_critical_issues ? (
                          <Badge className="bg-red-100 text-red-700">Sì</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">No</Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Status Change */}
                    {detail.staff_status_display && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-gray-500 mb-2">Cambia Staff Status:</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(detail.staff_status_display).map(([status, config]) => (
                            <Button
                              key={status}
                              variant="outline"
                              size="sm"
                              className={`text-xs ${
                                detail.dashboard_row.staff_status === status 
                                  ? 'ring-2 ring-umbria-olive' 
                                  : ''
                              }`}
                              onClick={() => handleStatusChange(status)}
                              disabled={statusUpdating}
                            >
                              {config.label_short}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Journey Info */}
              {detail.journey && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Info Cliente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Nome</p>
                        <p className="font-medium">{detail.journey.client_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Email</p>
                        <p className="font-medium">{detail.journey.client_email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Stato Cliente</p>
                        <p className="font-medium">{detail.journey.status || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Base</p>
                        <p className="font-medium">{detail.journey.accommodation_area || detail.journey.base || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Date</p>
                        <p className="font-medium">
                          {detail.journey.travel_dates?.start?.slice(0,10) || 'N/A'} →{' '}
                          {detail.journey.travel_dates?.end?.slice(0,10) || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* ========== ROUTING OVERVIEW ========== */}
              <RoutingOverview 
                journeyCode={journeyCode} 
                token={token}
              />
              
              {/* Requests */}
              {detail.requests && detail.requests.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MessageSquare size={14} />
                      Richieste ({detail.requests.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[200px] overflow-auto">
                      {detail.requests.map((req, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <Badge className="text-[10px]">{req.request_type}</Badge>
                            <span className="text-gray-400">{req.created_at?.slice(0,10)}</span>
                          </div>
                          <p className="text-gray-600">{req.item_title}</p>
                          {req.message && (
                            <p className="text-gray-500 mt-1 italic">"{req.message}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Recent Notifications */}
              {detail.recent_notifications && detail.recent_notifications.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText size={14} />
                      Notifiche Recenti ({detail.recent_notifications.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[150px] overflow-auto">
                      {detail.recent_notifications.map((notif, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded text-xs flex items-center justify-between">
                          <div>
                            <span className="text-gray-600">{notif.subject}</span>
                          </div>
                          <Badge className={
                            notif.status === 'sent' ? 'bg-green-100 text-green-700' :
                            notif.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {notif.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Link to customer view */}
              <div className="text-center pt-4">
                <a 
                  href={`/il-mio-viaggio/${journeyCode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-umbria-olive hover:underline inline-flex items-center gap-1"
                >
                  Apri vista cliente
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ============== MAIN COMPONENT ==============

export default function JourneyDashboard() {
  const token = useAdminToken();
  
  // State
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    staff_status: null,
    has_open_requests: null,
    not_fully_confirmed: null,
    upcoming_days: null
  });
  
  // Detail modal
  const [selectedJourney, setSelectedJourney] = useState(null);
  
  // Fetch status options
  useEffect(() => {
    if (!token) return;
    
    fetch(`${API_URL}/api/admin/journey-dashboard/config/statuses`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setStatusOptions(data.statuses || []);
      })
      .catch(() => {});
  }, [token]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey-dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [token]);
  
  // Fetch rows
  const fetchRows = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.staff_status) params.append('staff_status', filters.staff_status);
      if (filters.has_open_requests) params.append('has_open_requests', 'true');
      if (filters.not_fully_confirmed) params.append('not_fully_confirmed', 'true');
      if (filters.upcoming_days) params.append('upcoming_days', filters.upcoming_days);
      
      const url = `${API_URL}/api/admin/journey-dashboard?${params.toString()}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Errore nel caricamento');
      
      const data = await res.json();
      setRows(data.rows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);
  
  // Initial fetch
  useEffect(() => {
    fetchStats();
    fetchRows();
  }, [fetchStats, fetchRows]);
  
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      staff_status: null,
      has_open_requests: null,
      not_fully_confirmed: null,
      upcoming_days: null
    });
  };
  
  // Refresh
  const handleRefresh = () => {
    fetchStats();
    fetchRows();
  };
  
  if (!token) {
    return (
      <div className="text-center py-12 text-gray-500">
        Autenticazione admin richiesta
      </div>
    );
  }
  
  return (
    <div className="p-4" data-testid="journey-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-umbria-charcoal">
            Journey Operations Dashboard
          </h1>
          <p className="text-sm text-gray-500">
            Monitoraggio operativo viaggi
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
          className="gap-1"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Aggiorna
        </Button>
      </div>
      
      {/* Stats */}
      <StatsCard stats={stats} loading={statsLoading} />
      
      {/* Filters */}
      <FiltersBar 
        filters={filters}
        onFilterChange={handleFilterChange}
        statusOptions={statusOptions}
        onReset={handleResetFilters}
      />
      
      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-umbria-olive" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">
          Errore: {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nessun journey trovato con i filtri selezionati
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header row */}
          <div className="flex items-center gap-3 px-3 py-2 text-xs text-gray-500 border-b">
            <div className="flex-1 min-w-[200px]">Codice / Cliente</div>
            <div className="w-[140px]">Staff Status</div>
            <div className="w-[120px]">Date</div>
            <div className="w-[100px]">Base</div>
            <div className="w-[120px]">Conferme</div>
            <div className="w-[80px]">Richieste</div>
            <div className="w-[80px]">Fornitori</div>
            <div className="w-[80px]"></div>
          </div>
          
          {/* Rows */}
          {rows.map((journey, idx) => (
            <JourneyRow 
              key={journey.journey_code || idx}
              journey={journey}
              onViewDetail={setSelectedJourney}
            />
          ))}
        </div>
      )}
      
      {/* Detail Modal */}
      {selectedJourney && (
        <JourneyDetailModal
          journeyCode={selectedJourney}
          onClose={() => setSelectedJourney(null)}
          token={token}
        />
      )}
    </div>
  );
}
